import React, { MutableRefObject, RefObject, useEffect } from 'react';

import { DomHelper } from '@bryntum/gantt';
import { BryntumSchedulerPro } from '@bryntum/gantt-react';

import { useThemeStatusStore } from 'store/themeStatus.store';

import { useSchedulerLogic } from 'components/common/bryntumScheduler/Scheduler.logic';
import 'components/common/bryntumScheduler/Scheduler.scss';
import { useSchedulerConfig } from 'components/common/bryntumScheduler/SchedulerConfigHook';
import { SchedulerControls } from 'components/common/bryntumScheduler/SchedulerControlsHook';
import { DataConverterContract, HeaderLabel } from 'components/common/bryntumScheduler/types';
import { getTimelineSpanWithPadding } from 'components/common/bryntumScheduler/utils/date';

interface Props {
    data: any;
    controlsRef: MutableRefObject<SchedulerControls>;
    schedulerInstanceRef: RefObject<BryntumSchedulerPro>;
    dataConvertor?: (data: any) => DataConverterContract;
    headerTimelineLabels?: HeaderLabel[];
}

export const SchedulerView = ({
    data,
    controlsRef,
    schedulerInstanceRef,
    dataConvertor,
    headerTimelineLabels = undefined,
}: Props) => {
    const { themeStatus } = useThemeStatusStore();
    const { logic } = useSchedulerLogic({ data, dataConvertor, controlsRef, schedulerInstanceRef });

    const { paddedEnd, paddedStart } = getTimelineSpanWithPadding(
        logic.schedulerFormatedData.startDate,
        logic.schedulerFormatedData.finishDate,
    );

    const { schedulerConfig } = useSchedulerConfig(paddedStart, paddedEnd, controlsRef, schedulerInstanceRef);

    // light/dark mode
    useEffect(() => {
        DomHelper.setTheme(themeStatus ? 'classic-dark' : 'stockholm').then((context: any) => {
            if (context) {
                const { theme, prev } = context;
                document.body.classList.remove(`b-theme-${prev}`);
                document.body.classList.add(`b-theme-${theme}`);
            }
        });
    }, [themeStatus]);

    if (!logic.schedulerFormatedData.isValidDateRange || !paddedStart || !paddedEnd) {
        return <div>Date range is invalid</div>;
    }

    return (
        <div className={'scheduler-container'}>
            <BryntumSchedulerPro
                ref={schedulerInstanceRef}
                resources={logic.schedulerFormatedData.resources}
                events={logic.schedulerFormatedData.events}
                startDate={new Date(paddedStart)}
                endDate={new Date(paddedEnd)}
                timeRanges={headerTimelineLabels}
                {...schedulerConfig}
            />
        </div>
    );
};
